/**
 * RegionalCrossProgramHeatmap.jsx
 *
 * Heatmap showing cross-program collaboration for a specific region.
 * Each cell shows how many results are shared between a pair of programs
 * via Contributing Initiatives.
 */

import { useState, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/**
 * Extract a short program code (e.g. "SP03") from initiative text.
 */
function extractCode(entry) {
  if (!entry || typeof entry !== 'string') return null;
  const trimmed = entry.trim();
  const match = trimmed.match(/^(SP\d{2}|INIT-\d{2}|SGP-\d{2})/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Parse contributingInitiatives — array or " ; "-delimited string.
 */
function parseContributing(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    return raw.split(/\s*;\s*/).filter(Boolean);
  }
  return [];
}

/**
 * Interpolate from white to the given hex colour based on t (0..1).
 */
function colorScale(hex, t) {
  const num = parseInt(hex.replace('#', ''), 16);
  const rBase = (num >> 16) & 0xff;
  const gBase = (num >> 8) & 0xff;
  const bBase = num & 0xff;
  const r = Math.round(255 + (rBase - 255) * t);
  const g = Math.round(255 + (gBase - 255) * t);
  const b = Math.round(255 + (bBase - 255) * t);
  return `rgb(${r},${g},${b})`;
}

const CELL = 48;
const LABEL_W = 52;
const LABEL_H = 80;
const PAD = 2;

const RegionalCrossProgramHeatmap = memo(function RegionalCrossProgramHeatmap({ results, color }) {
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  /* ---------- build co-occurrence matrix ---------- */
  const matrixData = useMemo(() => {
    if (!results || results.length === 0) return null;

    const edgeMap = {}; // "SP01|SP03" -> count
    const programSet = new Set();

    for (const r of results) {
      const home = extractCode(r.initiative || r.Initiative || '');
      if (!home) continue;

      const contribs = parseContributing(
        r.contributingInitiatives || r['Contributing Initiatives'] || r.contributingPrograms
      );

      for (const entry of contribs) {
        const code = extractCode(entry);
        if (!code || code === home) continue;

        programSet.add(home);
        programSet.add(code);

        const key = [home, code].sort().join('|');
        edgeMap[key] = (edgeMap[key] || 0) + 1;
      }
    }

    const programs = Array.from(programSet).sort();
    if (programs.length < 2) return null;

    const indexMap = {};
    programs.forEach((p, i) => { indexMap[p] = i; });

    const n = programs.length;
    const matrix = Array.from({ length: n }, () => new Array(n).fill(0));
    let maxVal = 0;

    for (const [key, count] of Object.entries(edgeMap)) {
      const [a, b] = key.split('|');
      const ai = indexMap[a];
      const bi = indexMap[b];
      if (ai !== undefined && bi !== undefined) {
        matrix[ai][bi] = count;
        matrix[bi][ai] = count;
        if (count > maxVal) maxVal = count;
      }
    }

    return { programs, matrix, maxVal };
  }, [results]);

  /* ---------- render ---------- */
  if (!matrixData) {
    return (
      <ChartWrapper title="Cross-Program Collaboration">
        <NoChartData
          message="No cross-program collaboration data"
          detail="This heatmap shows how programs collaborate on results for this region via Contributing Initiatives. No cross-program links were found in the current selection."
        />
      </ChartWrapper>
    );
  }

  const { programs, matrix, maxVal } = matrixData;
  const n = programs.length;
  const svgW = LABEL_W + n * (CELL + PAD);
  const svgH = LABEL_H + n * (CELL + PAD);

  const exportData = [];
  for (let ri = 0; ri < n; ri++) {
    for (let ci = ri + 1; ci < n; ci++) {
      if (matrix[ri][ci] > 0) {
        exportData.push({
          'Program A': programs[ri],
          'Program B': programs[ci],
          'Shared Results': matrix[ri][ci],
        });
      }
    }
  }

  return (
    <ChartWrapper
      title="Cross-Program Collaboration"
      info="Heatmap showing how programs co-contribute to results for this region. Darker cells indicate more shared results via Contributing Initiatives."
      chartData={exportData}
      exportFilename="regional-cross-program-heatmap"
    >
      <div className="flex justify-center overflow-x-auto relative">
        <svg
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="w-full"
          style={{ maxWidth: Math.min(svgW, 700), height: 'auto' }}
        >
          {/* Column labels (top) — rotated -45 degrees with textAnchor="start" */}
          {programs.map((p, ci) => {
            const x = LABEL_W + ci * (CELL + PAD) + CELL / 2;
            const y = LABEL_H - 4;
            return (
              <text
                key={`col-${ci}`}
                x={x}
                y={y}
                textAnchor="start"
                fontSize={10}
                fontWeight={600}
                fill="#374151"
                transform={`rotate(-45, ${x}, ${y})`}
              >
                {p}
              </text>
            );
          })}

          {/* Row labels (left) */}
          {programs.map((p, ri) => (
            <text
              key={`row-${ri}`}
              x={LABEL_W - 6}
              y={LABEL_H + ri * (CELL + PAD) + CELL / 2 + 4}
              textAnchor="end"
              fontSize={10}
              fontWeight={600}
              fill="#374151"
            >
              {p}
            </text>
          ))}

          {/* Cells */}
          {programs.map((rowProg, ri) =>
            programs.map((colProg, ci) => {
              const val = matrix[ri][ci];
              const t = maxVal > 0 ? val / maxVal : 0;
              const cellColor = val > 0 ? colorScale(color, t) : '#f9fafb';
              const cx = LABEL_W + ci * (CELL + PAD);
              const cy = LABEL_H + ri * (CELL + PAD);

              return (
                <g key={`cell-${ri}-${ci}`}>
                  <rect
                    x={cx}
                    y={cy}
                    width={CELL}
                    height={CELL}
                    rx={4}
                    fill={ri === ci ? '#f3f4f6' : cellColor}
                    stroke="#e5e7eb"
                    strokeWidth={0.5}
                    style={{ cursor: ri !== ci && val > 0 ? 'pointer' : 'default' }}
                    onMouseEnter={(e) => {
                      if (ri === ci) return;
                      setTip({ row: rowProg, col: colProg, count: val });
                      setTipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setTip(null)}
                  />
                  {val > 0 && ri !== ci && (
                    <text
                      x={cx + CELL / 2}
                      y={cy + CELL / 2 + 4}
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={600}
                      fill={t > 0.55 ? '#fff' : '#374151'}
                      pointerEvents="none"
                    >
                      {val}
                    </text>
                  )}
                </g>
              );
            })
          )}
        </svg>

        {/* Tooltip — fixed position div, follows cursor */}
        {tip && (
          <div
            className="fixed z-50 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg border border-gray-200 pointer-events-none"
            style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}
          >
            <div className="font-semibold mb-1">{tip.row} &#8596; {tip.col}</div>
            <div>Shared results: <strong>{tip.count.toLocaleString()}</strong></div>
          </div>
        )}
      </div>

      {/* Gradient legend */}
      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
        <span>0</span>
        <div
          className="h-3 rounded"
          style={{
            width: 120,
            background: `linear-gradient(to right, #fff, ${color})`,
            border: '1px solid #e5e7eb',
          }}
        />
        <span>{maxVal}</span>
        <span className="ml-2 text-gray-400">shared results</span>
      </div>
    </ChartWrapper>
  );
});

RegionalCrossProgramHeatmap.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
};

export default RegionalCrossProgramHeatmap;
